/** API client. Types mirror the backend response shapes. Calls go through `lib/http`. */
import { BASE, enc, path, request } from "./http";

export interface Candidate {
  path: string;
  file_name: string;
  doc_type: string | null;
  /** index_row | content_scan | index_and_content. Both routes agreeing is the strongest signal. */
  resolution: string;
  confidence: number;
  /** Pages the content scan read the tag on. Empty when only the index named this document. */
  found_on_pages: number[];
  /** Whether the backend proposes reading this one. */
  recommended: boolean;
  title?: string | null;
  vendor?: string | null;
}

export interface SelectedDocument {
  path: string;
  doc_type: string | null;
  resolution?: string | null;
}

export interface Job {
  id: string;
  tag: string;
  package: string;
  status: string;
  stage: string;
  candidates: Candidate[] | null;
  /** The first confirmed document. Kept for older rows; prefer selected_documents. */
  selected_document: string | null;
  selected_documents: SelectedDocument[] | null;
  error: string | null;
}

export interface Provenance {
  /** Which document the shown value came from. A record spans several. */
  document_id: string | null;
  source_path: string | null;
  page_number: number | null;
  /** Null when the value could not be located. Never a placeholder. */
  box: { x: number; y: number; width: number; height: number } | null;
  match_type: string | null;
  agreement: boolean | null;
  ambiguous: boolean;
  ocr_confidence: number | null;
}

export interface Alternative {
  value: string;
  document_id: string;
  doc_type: string;
  confidence: number;
  source_path: string;
  page_number: number | null;
  box: { x: number; y: number; width: number; height: number } | null;
}

export interface Field {
  schema_version: string;
  name: string;
  value: string | null;
  not_stated: boolean;
  confidence: number;
  /** Document type the shown value was taken from. */
  chosen_from: string | null;
  /** How many separate documents stated this value. Two agreeing is the strongest signal. */
  corroborating_sources: number;
  /** Documents disagreed. Both values are kept and a person decides. */
  in_conflict: boolean;
  needs_review: boolean;
  provenance: Provenance;
  alternatives: Alternative[];
  edit?: unknown | null;
  /** A person's choice between conflicting values, with what it replaced. Absent until made. */
  override?: FieldOverride | null;
}

/** Who chose a field's value, which document it came from, and what the documents put first. */
export interface FieldOverride {
  value: string;
  document_id: string | null;
  machine_value: string | null;
  by: string;
  at: string;
  note: string | null;
}

export interface ResultSource {
  document_id: string;
  doc_type: string;
  source_path: string;
}

export interface Result {
  job_id: string;
  version: number;
  model: string;
  fields: Field[];
  needs_review: string[];
  sources: ResultSource[];
  /** Fraction of the five identity fields answered across every document read. */
  completeness: number;
  /** Every choice made on this extraction, oldest first. */
  overrides?: (FieldOverride & { field: string })[];
}

export interface PackageScanStatus {
  package: string;
  status: string;
  documents_scanned: number;
  documents_total: number;
  tags_known: number;
  /** Index tags read in content: index coverage only. */
  tags_found: number;
  /** Tags the content names that the index does not, counted apart (feat/all-tags). */
  content_tags_found?: number;
  error: string | null;
}

/** One document in estate search, with the pages that carry the value. */
export interface EstateSearchHit {
  package: string;
  path: string;
  file_name: string;
  pages: number[];
  /** One page viewer link per page. */
  links: { page: number; url: string }[];
  /** tag_index and/or page_text: which stored sweep output matched. */
  found_in: string[];
}

/** GET /search. An empty result names what was searched, so it is not read as "not in the estate". */
export interface EstateSearch {
  query: string;
  normalized: string;
  results: EstateSearchHit[];
  packages_searched: string[];
  packages_searched_count: number;
  packages_not_swept: string[];
  /** Searched, but swept before page text was kept, so only partly. The message says so. */
  packages_text_incomplete: string[];
  truncated: boolean;
  message: string;
}

/** Every call goes through the one request core in `lib/http`, so its rules are the same here. */
const call = request;

export const api = {
  listJobs: () => call<Job[]>("/uc1/jobs"),
  getJob: (id: string) => call<Job>(path`/uc1/jobs/${id}`),
  createJob: (tag: string, pkg: string) =>
    call<Job>("/uc1/jobs", { method: "POST", body: JSON.stringify({ tag, package: pkg }) }),
  /** Confirm the set of documents to read. A tag's identity is spread across several. */
  confirm: (id: string, paths: string[]) =>
    call<Job>(path`/uc1/jobs/${id}/confirm`, { method: "POST", body: JSON.stringify({ paths }) }),
  result: (id: string) => call<Result>(path`/uc1/jobs/${id}/result`),
  /** Find a tag or part number across every swept package the signed-in person may read. */
  search: (q: string) => call<EstateSearch>(`/search?q=${enc(q)}`),
  buildScan: (pkg: string) =>
    call<PackageScanStatus>(path`/uc1/packages/${pkg}/scan`, { method: "POST" }),
};

export interface PackageSummary {
  name: string; adapter: string; documents: number;
  index_rows: number; index_requires_ocr: boolean;
}

export interface DocumentRow {
  /** Opaque, from the path below the package: the document view's address carries it. */
  id: string;
  path: string; file_name: string;
  /** Folders below the package, so two files of one name in different volumes can be told apart. */
  folder: string;
  doc_type: string | null;
  /** `master index` when one index row describes the file, `not in index` otherwise. */
  found_via: string;
  approved: boolean; databook: string | null; vendor: string | null; title: string | null;
}

/** A tag box on a page, as fractions of the page. */
export interface TagBox { x: number; y: number; width: number; height: number }

/** One tag the content sweep read on one page, and where. */
export interface PageTag {
  tag: string; kind: string; boxes: TagBox[];
  /** `box` drawn; `not_found` named but not placed; `not_recorded` swept before boxes were kept. */
  located: "box" | "not_found" | "not_recorded";
  /** False when the package's master index does not name the tag. */
  in_index?: boolean;
}

/** One cell of a table row: its value as printed, and where the OCR read it, if anywhere. */
export interface RowCell {
  header: string; value: string;
  /** The field the column feeds: tag, make, model, serial, service, calibration_range, part_number. */
  field: string | null;
  box: TagBox | null;
  /** Share of the value's letters and digits the boxed words account for, 0 to 1. */
  coverage: number;
}

/** One row read from a table page by the package tag run, each value cited. */
export interface TableRowView {
  id: string; package: string; document_id: string; path: string; file_name: string;
  page: number; row_index: number; tag: string; tag_key: string; row_class: string | null;
  make: string | null; model: string | null; serial: string | null; service: string | null;
  calibration_range: string | null; part_number: string | null;
  /** `verified`, or `tag_not_on_page`: kept for review, never written to the register. */
  status: string; confidence: number; reader: string;
  tag_box: TagBox | null; cells: RowCell[];
}

/** The package tag run (feat/all-tags): every page read for tags, each table read row by row. */
export interface TagRun {
  package: string; status: "running" | "ready" | "failed" | string;
  documents_total: number; documents_done: number; pages_read: number;
  tags_found: number; tags_not_in_index: number; table_pages: number;
  table_reads: number; table_reads_cached: number; table_pages_skipped: number;
  rows_verified: number; rows_flagged: number; assets_written: number;
  /** Documents that would not open, named; pages past the per-document limit, not read. */
  documents_failed?: number; failed_paths?: string[]; pages_beyond_limit?: number;
  error: string | null; work_state: string | null; table_reader: string;
}

/** A document, its index metadata, and the tags the content sweep read in it. */
export interface DocumentView extends DocumentRow {
  package: string; page_count: number;
  /** The package's content sweep: ready, building, failed, or none. */
  sweep: string;
  tags: { tag: string; kind: string; pages: number[]; in_index?: boolean; sources?: string[] }[];
  /** Only the pages a tag was read on. */
  pages: { page: number; tags: PageTag[] }[];
  /** Rows read from this document's tables. */
  table_rows?: TableRowView[];
}

export interface SpecEntry {
  name: string; value: string; source: string; confidence: number;
  override: { value: string; original: string; by: string } | null;
}

/** One source the obsolescence check read and found relevant, with the sentence it relied on. */
export interface ObsolescenceFinding {
  source_url: string;
  quote: string | null;
  authority: string;
  lifecycle: string | null;
}

/** What `POST /uc5/jobs/{id}/obsolescence` records on the job. */
export interface ObsolescenceResult {
  question: string;
  /** obsolete | announced | active | no_notice_found. Never a yes or no. */
  status: string;
  /** Two relevant sources pointing opposite ways. A person settles it. */
  disagreement: boolean;
  evidence: (ObsolescenceFinding & { successor_part: string | null }) | null;
  sources_read: number;
  sources_failed: number;
  considered: ObsolescenceFinding[];
}

/** One replacement option from the market search, as `ReplacementOption.to_json` writes it. */
export interface ReplacementCandidate {
  /** An SD-22 Table 9 resolution, for example simple_substitute or life_of_need_buy. */
  outcome: string;
  part_number: string | null;
  description: string | null;
  collateral: string[];
  collateral_count: number;
  quote: string | null;
  source_url: string;
  authority: string;
  available_until: string | null;
  /** Who makes the replacement, as the source names it. */
  manufacturer?: string | null;
  /** The maker is on Shell's approved vendor list (C8). A purchasing flag, never a technical one. */
  on_approved_vendor_list?: boolean;
}

/** One input to the life-of-need calculation and where the figure came from. */
export interface LtbInput { name: string; value: number | null; source: string | null }

/** `replacement.LifeOfNeed.to_json`: how many to buy, or `cannot_determine` and what is missing. */
export interface LtbRecommendation {
  status: "recommended" | "stock_covers_need" | "cannot_determine" | string;
  quantity: number | null;
  need: number | null;
  inputs: LtbInput[];
  missing: string[];
  explanation: string;
}

/** The last-time-buy decision stored on a UC5 job (C5). */
export interface LtbDecision {
  state: "proposed" | "approved" | "bridged" | "resolved" | "closed" | string;
  open: boolean;
  option: ReplacementCandidate;
  deadline: string | null;
  recommendation: LtbRecommendation;
  proposed_by: string;
  approved_by?: string;
  bought?: { quantity: number | null; by: string };
  resolved?: { resolution: string; by: string };
  closed?: { reason: string; by: string };
  history?: Omit<LtbDecision, "history">[];
}

export interface Obligation { kind: string; due: string; description: string; open: boolean }

export interface UC5Job {
  id: string; source_job_id: string | null; identity: Record<string, string>;
  stage: string; status: string; spec: SpecEntry[] | null;
  spec_approved_by: string | null;
  obsolescence: ObsolescenceResult | null;
  candidates: ReplacementCandidate[] | null;
  outreach_sent_by?: string | null;
  outreach_sent_at?: string | null;
  outreach_reply_due?: string | null;
  outreach_overdue?: boolean;
  /** Only on a market-search response: `loaded` or `not_loaded`. */
  approved_vendor_list?: string | null;
  ltb_decision?: LtbDecision | null;
  obligations?: Obligation[];
}

/** `GET /documents/locate`: the package and view id of a cited document. */
export interface DocumentLocation { package: string; id: string; path: string; file_name: string }

export const docs = {
  packages: () => call<PackageSummary[]>("/documents/packages"),
  documents: (name: string) =>
    call<DocumentRow[]>(path`/documents/packages/${name}/documents`),
  document: (name: string, id: string) =>
    call<DocumentView>(path`/documents/packages/${name}/documents/${id}`),
  /** Rows read from the package's tables, for one tag when given. */
  tableRows: (name: string, tag?: string) =>
    call<TableRowView[]>(path`/documents/packages/${name}/table-rows` + (tag ? `?tag=${enc(tag)}` : "")),
  tagRun: (name: string) => call<TagRun>(path`/documents/packages/${name}/tag-run`),
  /** Queue the package tag run. Engineers only; table pages are read on the subscription. */
  startTagRun: (name: string) => call<TagRun>(path`/documents/packages/${name}/tag-run`, { method: "POST" }),
  /** Where a cited document's view is. 404 when it is in no package this person may open. */
  locate: (c: { path?: string; key?: string; package?: string }) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(c)) if (v) q.set(k, v);
    return call<DocumentLocation>(`/documents/locate?${q.toString()}`);
  },
  approve: (paths: string[], approved: boolean) =>
    call<{ changed: number }>("/documents/approve", {
      method: "POST", body: JSON.stringify({ paths, approved }),
    }),
};

export const uc5 = {
  list: () => call<UC5Job[]>("/uc5/jobs"),
  get: (id: string) => call<UC5Job>(path`/uc5/jobs/${id}`),
  createManual: (b: { manufacturer?: string; model_number: string; part_number?: string; description?: string }) =>
    call<UC5Job>("/uc5/jobs", { method: "POST", body: JSON.stringify(b) }),
  fromJob: (from_job_id: string) =>
    call<UC5Job>("/uc5/jobs", { method: "POST", body: JSON.stringify({ from_job_id }) }),
  /** The approver is whoever is signed in; the API takes it from the session, not from here. */
  approveSpec: (id: string, overrides: { name: string; value: string }[] = []) =>
    call<UC5Job>(path`/uc5/jobs/${id}/approve-spec`, {
      method: "POST", body: JSON.stringify({ overrides }),
    }),
  /** Runs the research loop on the approved specification. 409 until the spec is approved. */
  obsolescence: (id: string) =>
    call<UC5Job>(path`/uc5/jobs/${id}/obsolescence`, { method: "POST" }),
  marketSearch: (id: string) =>
    call<UC5Job>(path`/uc5/jobs/${id}/market-search`, { method: "POST" }),
};

/** One site visit report that names a component, with the pages that name it. */
export interface VisitAppearance {
  date: string;
  title: string;
  unit: string;
  author: string;
  path: string;
  pages: number[];
}

/** Every site visit that named a component, oldest first. Deliberately carries no failure rate. */
export interface ComponentHistory {
  component: string;
  reports: number;
  years: number[];
  /** Named in more than one visit, in more than one year. A flag for a person, not a conclusion. */
  recurring: boolean;
  appearances: VisitAppearance[];
  /** Why the visit reports could not be searched; empty when they were. */
  unavailable?: string;
  /** Visit reports that resolved to a file, and how many of those have not been read yet. */
  indexed?: number;
  unread?: number;
}

export const maintenance = {
  history: (pkg: string, component: string) =>
    call<ComponentHistory>(
      `${path`/uc1/packages/${pkg}/history`}?component=${enc(component)}`,
    ),
};

export const FIELD_LABELS: Record<string, string> = {
  make_manufacturer: "Manufacturer",
  model_number: "Model number",
  part_number: "Part number",
  serial_number: "Serial number",
  vendor: "Vendor",
};

/** Document types, in the words an engineer uses for them. */
export const DOC_TYPE_LABELS: Record<string, string> = {
  data_sheet: "data sheet",
  ga_drawing: "GA drawing",
  test_certificate: "test certificate",
  instrument_list: "instrument list",
  om_manual: "O&M manual",
  component_entry: "component entry",
  quality_plan: "quality plan",
  bulletin: "bulletin",
  index: "index",
  unclassified: "unclassified document",
};

/** How a document came to be a candidate, and what that says about the evidence. */
export const RESOLUTION_LABELS: Record<string, string> = {
  index_row: "named in the master index",
  content_scan: "tag read in the document",
  index_and_content: "index and content agree",
};

/** URL for a rendered source page. Used by the viewer and by mismatch crops. */
export function pageImageUrl(source: string, page: number): string {
  return `${BASE}/uc1/documents/page?path=${enc(source)}&page=${page}`;
}

/** URL for a small render of a source page, for a page strip. */
export function pageThumbUrl(source: string, page: number): string {
  return `${pageImageUrl(source, page)}&thumb=true`;
}

/** What approving a review item accepts: a field's reading, a UC5 finding, or a gate's proposal. */
export interface ReviewShown {
  value?: string | null;
  confidence?: number | null;
  alternatives?: (string | null)[];
  document?: string | null;
  page?: number | null;
  /** The cited region on that page. Null when it was not located; never a placeholder. */
  box?: { x: number; y: number; width: number; height: number } | null;
  /** The UC1 job a flagged field belongs to. Absent on items decided before it was recorded. */
  job_id?: string;
  stage?: string;
  status?: string | null;
  quote?: string | null;
  source_url?: string | null;
  options?: { outcome?: string | null; part_number?: string | null; quote?: string | null; source_url?: string | null }[];
  gate?: string;
  documents?: string[];
  tag?: string | null;
  reasons?: string[];
  spec?: Record<string, unknown> | null;
  unresolved?: string[];
  /** A run that ended needing a person: the register fields to check. */
  needs_review?: string[];
  /** A lifecycle run that ended needing a person: what it found. */
  finding?: { status?: string | null; disagreement?: boolean; evidence?: { quote?: string | null } | null };
  /** An identity review: each value the approval accepts, read as a flagged field is. */
  fields?: (ReviewShown & { name: string | null })[];
}

/** One flagged value, UC5 job or gated run waiting on a person (G3). */
export interface ReviewItem {
  id: string;
  /** uc1_extraction | uc5_job | module_run */
  source_type: string;
  source_id: string;
  /** The flagged field, for a UC1 item. */
  field: string | null;
  tag: string | null;
  package: string | null;
  /** low_confidence | conflict | no_box | gate | needs_review */
  reason: string;
  /** pending | approved | rejected | returned */
  state: string;
  flagged_at: string;
  age_seconds: number;
  /** What an approval accepts, for a pending item. Null when there is nothing to show. */
  shown?: ReviewShown | null;
  decision: {
    decision: string;
    /** The signed-in person who decided, recorded by the API from the session. */
    decided_by: string;
    decided_at: string;
    note: string | null;
  } | null;
}

export interface ReviewList {
  items: ReviewItem[];
  /** Items by state, over everything this user may see. */
  counts: Record<string, number>;
}

export type ReviewDecision = "approve" | "reject" | "return";


/** The cut below which a field is flagged for a person (G4). */
export interface ReviewThreshold {
  value: number;
  default: number;
  min: number;
  max: number;
  /** False until the A12 calibration report is published; the value is then shown read only. */
  editable: boolean;
  changed_by: string | null;
  changed_at: string | null;
}

export const settings = {
  reviewThreshold: () => call<ReviewThreshold>("/settings/review-threshold"),
};
